import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import VLFModal from "@/components/Modal";
import CreateSelectEntity from "@/components/CreateSelectEntity";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";

import {PlayerAPI} from "@/api/player";

import {AREA_OPTIONS, sideOptions} from "@/utils/constants";
import {
	buildPlayerBody,
	checkRequiredFields,
	getTextField,
	playerToOption,
} from "@/utils/functions";
import {useTranslation} from "react-i18next";

import "./handlePlayer.scss";

function HandlePlayerModal(props) {
	const {
		open,
		initialData,
		confirm,
		close,
		title,
		includeInternal = false,
	} = props;

	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState([]);

	// Si parte dalla ricerca: il giocatore osservato spesso è già nel database.
	const [saveOption, setSaveOption] = useState("select");

	const [players, setPlayers] = useState([]);
	const [playersLoading, setPlayersLoading] = useState(false);
	const [playerSearch, setPlayerSearch] = useState("");

	const createPlayer = async () => {
		try {
			const body = buildPlayerBody({
				firstName: formData?.firstName,
				lastName: formData?.lastName,
				dateOfBirth: formData?.dateOfBirth,
				favoriteFoot: formData?.mainFoot?.value,
				goalkeeper: formData?.playerPrimaryRole?.value === "Goalkeeper",
				primaryRole: formData?.playerPrimaryRole?.label,
			});

			const response = await PlayerAPI.create(body);
			return response;
		} catch (error) {
			throw error;
		}
	};

	const getRequiredFields = () => {
		if (saveOption === "select") {
			return ["player"];
		} else {
			return ["lastName", "playerPrimaryRole"];
		}
	};

	// Ricerca lato server: la lista dei prospect puo' essere molto lunga.
	useEffect(() => {
		if (saveOption !== "select" || !seasonId) {
			return;
		}

		let active = true;
		setPlayersLoading(true);

		const timer = setTimeout(async () => {
			try {
				const response = await PlayerAPI.paginated({
					prospect: includeInternal ? undefined : true,
					seasonId,
					search: playerSearch.trim() || undefined,
					page: 1,
					pageSize: 25,
				});
				if (active) setPlayers(response?.data || []);
			} catch {
				if (active) setPlayers([]);
			} finally {
				if (active) setPlayersLoading(false);
			}
		}, 300);

		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [saveOption, seasonId, playerSearch, includeInternal]);

	useEffect(() => {
		if (!open) {
			setFormData({});
		}
	}, [open]);

	const defaultTextField = {
		values: formData,
		setValues: setFormData,
		errors,
		setErrors,
	};

	const selectedPlayer = formData?.playerData;

	// Nome dai dati del giocatore: la label del select include anche la squadra.
	const selectedName =
		[selectedPlayer?.firstName, selectedPlayer?.lastName]
			.filter(Boolean)
			.join(" ") || formData?.player?.label;

	const initials =
		[selectedPlayer?.firstName, selectedPlayer?.lastName]
			.filter(Boolean)
			.map((part) => part[0]?.toUpperCase())
			.join("") || (selectedName || "?")[0]?.toUpperCase();

	const birthYear = selectedPlayer?.dateOfBirth
		? moment(selectedPlayer.dateOfBirth).year()
		: null;

	const summaryChips = [
		birthYear ? "Leva " + birthYear : null,
		selectedPlayer?.goalkeeper
			? "Portiere"
			: selectedPlayer?.primaryRole || null,
		selectedPlayer?.roster?.club?.name ||
			selectedPlayer?.club?.name ||
			selectedPlayer?.clubName ||
			null,
	].filter(Boolean);

	return (
		<VLFModal
			maxWidth="md"
			open={open}
			title={
				initialData
					? "Modifica informazione"
					: title || "Aggiungi obiettivo scouting"
			}
			confirmLabel={initialData ? "Salva" : "Aggiungi"}
			close={close}
			confirm={async () => {
				const errors = checkRequiredFields(getRequiredFields(), formData);
				setErrors(errors);

				if (!errors.length) {
					let playerData = {};
					if (saveOption === "new") {
						let playerResponse;
						try {
							playerResponse = await createPlayer();
						} catch {
							setErrors(["creation"]);
							return;
						}
						if (!playerResponse?.id) return;
						playerData = {playerId: playerResponse?.id};
					} else {
						playerData = {playerId: formData?.player?.value};
					}
					confirm({...playerData}, initialData?.id);
				}
			}}
			content={
				<div className="row">
					{errors.includes("creation") && (
						<p role="alert">
							Impossibile creare il giocatore. Verifica i dati e riprova.
						</p>
					)}
					<div className="col-12">
						<CreateSelectEntity
							currentOption={saveOption}
							setCurrentOption={setSaveOption}
						/>
					</div>
					<p className="col-12 hp-hint">
						{saveOption === "new"
							? "Il giocatore viene creato nel database scouting e aggiunto agli obiettivi della gara."
							: "Cerca in tutto il database, scouting e interni."}
					</p>
					{saveOption === "new" ? (
						<>
							<div className="col-12 col-sm-6">
								{getTextField({
									...defaultTextField,
									fieldKey: "firstName",
									label: t("name"),
								})}
							</div>
							<div className="col-12 col-sm-6">
								{getTextField({
									...defaultTextField,
									fieldKey: "lastName",
									label: "Cognome",
									required: true,
								})}
							</div>
							<div className="col-12 col-sm-6">
								<VLFDatePicker
									label="Data di nascita"
									value={formData.dateOfBirth}
									onChange={(value) => {
										setFormData({...formData, dateOfBirth: value});
									}}
									format="DD-MM-YYYY"
								/>
							</div>
							<div className="col-12 col-sm-6">
								<VLFSelectNew
									options={AREA_OPTIONS}
									sorted={false}
									value={formData.playerPrimaryRole}
									onChange={(newValue) => {
										setFormData({
											...formData,
											playerPrimaryRole: newValue,
										});
									}}
									label="Ruolo"
									required
								/>
							</div>
							<div className="col-12 col-sm-6">
								<VLFSelectNew
									options={sideOptions(t)}
									sorted={false}
									value={formData.mainFoot}
									onChange={(newValue) =>
										setFormData({...formData, mainFoot: newValue})
									}
									label="Piede principale"
									isClearable
								/>
							</div>
						</>
					) : (
						<>
							<div className="col-12">
								<VLFSelectNew
									options={players.map((pp) => playerToOption(pp, true))}
									isLoading={playersLoading}
									filterOption={null}
									onInputChange={(value, action) => {
										if (action.action === "input-change") {
											setPlayerSearch(value);
										}
									}}
									placeholder="Cerca un giocatore per nome o cognome..."
									noOptionsMessage={() =>
										playerSearch.trim()
											? "Nessun giocatore trovato"
											: "Digita per cercare un giocatore"
									}
									sortKey="sortKey"
									value={formData?.player}
									onChange={(newValue) => {
										setErrors([]);
										const picked = players.find(
											(pp) => String(pp.id) === String(newValue?.value),
										);
										setFormData({
											...formData,
											player: newValue,
											playerData: picked || formData?.playerData,
										});
									}}
									label="Giocatore"
									required
									error={errors.indexOf("player") !== -1}
								/>
							</div>
							{formData?.player && (
								<div className="col-12">
									<div className="hp-player-summary">
										<div
											className="hp-player-summary__avatar"
											aria-hidden="true"
										>
											{initials}
										</div>
										<div className="hp-player-summary__info">
											<span className="hp-player-summary__name">
												{selectedName}
											</span>
											<div className="hp-player-summary__chips">
												{summaryChips.length ? (
													summaryChips.map((chip) => (
														<span className="hp-chip" key={chip}>
															{chip}
														</span>
													))
												) : (
													<span className="hp-player-summary__empty">
														Nessun dato anagrafico registrato
													</span>
												)}
											</div>
										</div>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			}
		/>
	);
}

export default HandlePlayerModal;
